import {
	BCComboBoxRef,
	BCDivider,
	BCLoading,
	BCMultipleCombobox,
	findMessage,
	IComboboxSelectedChangeEventArgs
} from '@bimser/components';
import { FormItemTypes, IItem } from '@bimser/core';
import IconRefresh from '@bimser/icons/16/refresh';
import { Map } from 'immutable';
import * as React from 'react';
import { ICSSProperties, IPropertyField, MultiLanguageText } from '../../../../entities/common';
import ListBoxBase from '../../base/ListBoxBase';
import Styles from './assets/style.scss';
import { ITagBoxProperties } from './entities/ITagBoxProperties';
const dropdownRenderDivider: ICSSProperties = { margin: '0' };
import _merge from 'lodash/merge';

export default class TagBox extends ListBoxBase {
	allowClear: boolean;
	placeholder: MultiLanguageText;
	inputRef = React.createRef<BCComboBoxRef>();
	hideForceRefreshButton: boolean;

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.TagBox;
		this.allowClear = false;
		this.hideForceRefreshButton = false;

		this.__dropdownRender = this.__dropdownRender.bind(this);
		this.__renderDropdownFooter = this.__renderDropdownFooter.bind(this);
		this.allowShowSearch = this.allowShowSearch.bind(this);
		this.__getItems = this.__getItems.bind(this);
		this.focus = this.focus.bind(this);

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			const selectedKeys: string[] = JSON.parse(value) as string[];
			this.__proxyChanging.selectedItemValues = selectedKeys.map(Number);
			this.__proxyChanging.items = this.items.map(item =>
				_merge(item, { selected: selectedKeys.includes(item.key) })
			);
		});
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'placeholder'];
	}

	initialize() {
		super.initialize();
		this.placeholder = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'placeholder', culture, existCultures);
			}
		);
	}

	allowShowSearch() {
		if (this.__isReadOnly() || this.__isDisabled()) {
			return false;
		}
		return this.dataSource.name ? true : this.showSearch;
	}

	__loadProperties(data: ITagBoxProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.allowClear = data.allowClear;
		this.__proxyChanged.hideForceRefreshButton = data.hideForceRefreshButton;

		if (data.placeholder) {
			this.placeholder.setData(data.placeholder);
		}
	}

	reload(forceRefresh?: boolean) {
		if (this.dataSource?.name) {
			if (this.dataSource?.parameters.length) {
				this.__dataSourceValueEntryFlag = false;
				this.__reloadDataSource(this.dataSource, forceRefresh).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: true, forceRefresh });
			}
		}
	}

	private triggerValueChanged(args: IComboboxSelectedChangeEventArgs) {
		const selectedKeys: string[] = args.data as string[];
		this.__proxyChanging.selectedItemValues = selectedKeys.map(Number);
		this.__proxyChanging.items = this.items.map(item => {
			const selected = selectedKeys?.findIndex(i => i === item.value.toString()) > -1;
			return Object.assign({}, item, { selected });
		});
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields(properties);
		return [
			...fields,
			{
				name: 'allowClear',
				type: 'boolean'
			},
			{
				name: 'hideForceRefreshButton',
				type: 'boolean'
			}
		];
	}

	__getItems(): IItem[] {
		return this.__sortItems(
			Object.values(this.__itemsCollection).map(i => {
				const selected = this.selectedItemValues?.includes(i.value);
				return {
					key: i.key,
					value: i.value?.toString(),
					selected,
					text: i.text.getText()
				};
			})
		);
	}

	__renderDropdownFooter() {
		if (!this.hideForceRefreshButton) {
			return (
				<>
					<BCDivider styles={dropdownRenderDivider} />
					<div className={Styles.dropdownRender}>
						<a onClick={() => this.reload(true)}>
							<IconRefresh />
							{findMessage.get('102660')}
						</a>
					</div>
				</>
			);
		}
	}

	__dropdownRender(menu: React.ReactElement) {
		return (
			<div>
				{menu}
				{this.__renderDropdownFooter()}
			</div>
		);
	}

	__getComponentData() {
		return {
			...super.__getComponentData(),
			allowClear: this.allowClear && !this.readOnly,
			hideForceRefreshButton: this.hideForceRefreshButton,
			placeholder: this.placeholder.getData(),
			options: this.__getItems(),
			values: this.__getSelectedValues()
		};
	}

	focus() {
		this.inputRef.current?.focus();
	}

	__render() {
		return super.__render(
			<BCMultipleCombobox
				ref={this.inputRef}
				tabIndex={this.tabIndex}
				showSearch={this.allowShowSearch()}
				disabled={this.__isDisabled()}
				readonly={this.__isReadOnly()}
				placeholder={this.placeholder.getText()}
				options={this.__getItems()}
				value={this.__getSelectedValues()}
				allowClear={this.allowClear}
				onSelectedChange={this.triggerValueChanged.bind(this)}
				style={{
					width: '100%',
					borderRadius: '5px',
					...({ '--bgColor': this.style.backgroundColor } as any)
				}}
				className={this.style.backgroundColor ? Styles.TagBoxBackgroundClass : ''}
				mode="multiple"
				onSearch={this.dataSource.name ? this.__onSearch : null}
				notFoundContent={this.loading ? <BCLoading size="small" /> : null}
				dropdownRender={
					this.dataSource.name && !this.dataSource.runAtServer && this.__dropdownRender
				}
				dropdownClassName={Styles.TagBoxOptions}
				fromMobile={this.__form.__fromMobile}
			/>
		);
	}
}
